import React, { useState, useEffect, useRef, useCallback } from "react";
import API_BASE_URL from "../RestAPI";
import {
  FiZap, FiGitBranch, FiCopy, FiCheck, FiX, FiExternalLink, FiUploadCloud,
  FiAlertTriangle, FiRefreshCw, FiChevronDown, FiChevronUp, FiSlash,
  FiMessageSquare,
} from "react-icons/fi";
import "./AIFixPanel.css";

/* Statuses where a runner is still working — the panel polls while in these. */
const OPEN_STATUSES = ["queued", "analyzing", "patching", "verifying"];

/* The progress stepper. `match` maps a job status onto a step. */
const STEPS = [
  { key: "queued", label: "Queued", match: ["queued"] },
  { key: "analyzing", label: "Reading code", match: ["analyzing"] },
  { key: "patching", label: "Writing fix", match: ["patching"] },
  { key: "verifying", label: "Verifying", match: ["verifying"] },
  { key: "branch", label: "Branch created", match: ["branch_ready", "needs_review"] },
];

const STATUS_TONE = {
  queued: "info", analyzing: "info", patching: "info", verifying: "info",
  branch_ready: "success",
  needs_review: "warn", no_code_fix: "warn",
  failed: "danger", rejected: "danger", cancelled: "muted",
};

const CONFIDENCE_TONE = { high: "success", medium: "warn", low: "danger" };

const AIFixPanel = ({ error, jobId = null, onJobChange }) => {
  const [job, setJob] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState(null);
  const [mode, setMode] = useState("patch");
  const [showDiff, setShowDiff] = useState(true);
  const [showLog, setShowLog] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectNotes, setRejectNotes] = useState("");
  const [copied, setCopied] = useState("");
  const [conversation, setConversation] = useState([]);
  const [awaitingAnswers, setAwaitingAnswers] = useState(false);
  const [answers, setAnswers] = useState({});
  const [instruction, setInstruction] = useState("");
  const [askOpen, setAskOpen] = useState(false);

  const lastSeq = useRef(0);
  const pollRef = useRef(null);
  const logRef = useRef(null);
  const askRef = useRef(null);

  // Held in a ref so the polling effect does not depend on the callback's
  // identity — the parent recreates it on every render, which would otherwise
  // tear down and rebuild the interval continuously.
  const onJobChangeRef = useRef(onJobChange);
  useEffect(() => { onJobChangeRef.current = onJobChange; }, [onJobChange]);

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
  };

  const fingerprint = error?.error_fingerprint;
  const batchNumber = error?.batch_number || 1;

  const notify = (text, tone = "info") => {
    setMessage({ text, tone });
    setTimeout(() => setMessage(null), 6000);
  };

  /* ── Loading ────────────────────────────────────────────────────────── */

  // Full record including diff and transcript. Called once when a job appears
  // or finishes — never on the poll, which must stay light.
  const loadDetail = useCallback(async (jobId) => {
    try {
      const res = await fetch(`${API_BASE_URL}error-tracking/ai-fix/detail/?job_id=${jobId}`, { headers });
      const data = await res.json();
      if (data.status === "success") {
        setJob(data.data);
        setEvents(data.events || []);
        setConversation(data.conversation || []);
        setAwaitingAnswers(!!data.awaiting_answers);
        setAnswers({});
        lastSeq.current = data.events?.length ? data.events[data.events.length - 1].seq : 0;
        return data.data;
      }
    } catch (err) {
      console.error("AI fix detail error:", err);
    }
    return null;
  }, []);

  // Find the newest job for this error pattern. The list and detail APIs embed
  // `latest_ai_fix`, but this panel is also correct when opened cold.
  const loadLatest = useCallback(async () => {
    if (!fingerprint) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        error_fingerprint: fingerprint,
        batch_number: batchNumber,
        page_size: 1,
      });
      const res = await fetch(`${API_BASE_URL}error-tracking/ai-fix/list/?${params}`, { headers });
      const data = await res.json();
      if (data.status === "success" && data.data?.length) {
        await loadDetail(data.data[0].job_id);
      } else {
        setJob(null);
        setEvents([]);
        lastSeq.current = 0;
      }
    } catch (err) {
      console.error("AI fix list error:", err);
    } finally {
      setLoading(false);
    }
  }, [fingerprint, batchNumber, loadDetail]);

  useEffect(() => {
    setJob(null);
    setEvents([]);
    setConversation([]);
    setAwaitingAnswers(false);
    setAnswers({});
    setInstruction("");
    setAskOpen(false);
    setRejectOpen(false);
    setRejectNotes("");
    lastSeq.current = 0;
    if (jobId) {
      setLoading(true);
      loadDetail(jobId).finally(() => setLoading(false));
    } else {
      loadLatest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fingerprint, batchNumber, jobId, loadLatest, loadDetail]);

  /* ── Polling while a runner is working ──────────────────────────────── */

  useEffect(() => {
    const isOpen = job && OPEN_STATUSES.includes(job.status);
    if (!isOpen) {
      clearInterval(pollRef.current);
      return undefined;
    }

    const tick = async () => {
      try {
        const params = new URLSearchParams({ job_id: job.job_id, after_seq: lastSeq.current });
        const res = await fetch(`${API_BASE_URL}error-tracking/ai-fix/status/?${params}`, { headers });
        const data = await res.json();
        if (data.status !== "success") return;

        if (data.events?.length) {
          setEvents(prev => [...prev, ...data.events]);
          lastSeq.current = data.events[data.events.length - 1].seq;
        }
        if (data.conversation) setConversation(data.conversation);
        setAwaitingAnswers(!!data.awaiting_answers);

        const next = data.data;
        // The status poll deliberately omits the diff, so when the job leaves
        // the working states we re-fetch the full record to get it.
        if (!OPEN_STATUSES.includes(next.status)) {
          const full = await loadDetail(next.job_id);
          if (onJobChangeRef.current) onJobChangeRef.current(full || next);
        } else {
          setJob(prev => ({ ...prev, ...next }));
        }
      } catch (err) {
        console.error("AI fix poll error:", err);
      }
    };

    pollRef.current = setInterval(tick, 3000);
    return () => clearInterval(pollRef.current);
    // Only the job id and whether it is still open should control the interval;
    // re-running on every field change would reset the 3s cadence constantly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.job_id, job && OPEN_STATUSES.includes(job.status), loadDetail]);

  // Keep the live log pinned to the newest line.
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [events]);

  // Bring the form into view when it appears, so it is never opened off-screen.
  useEffect(() => {
    if ((askOpen || awaitingAnswers) && askRef.current) {
      askRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [askOpen, awaitingAnswers]);

  /* ── Actions ────────────────────────────────────────────────────────── */

  const enqueue = async (retryOf = null) => {
    if (!error?.id) {
      notify("Open this error from the Error List to queue a new attempt.", "danger");
      return;
    }
    setBusy("enqueue");
    try {
      const res = await fetch(`${API_BASE_URL}error-tracking/ai-fix/enqueue/`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          error_id: error.id,
          error_fingerprint: fingerprint,
          batch_number: batchNumber,
          mode,
          ...(retryOf ? { retry_of: retryOf } : {}),
        }),
      });
      const data = await res.json();
      if (data.status === "success") {
        await loadDetail(data.data.job_id);
        notify(data.message, data.created ? "success" : "info");
        if (onJobChange) onJobChange(data.data);
      } else {
        notify(data.message || "Could not queue the job", "danger");
      }
    } catch (err) {
      notify("Could not reach the server", "danger");
    } finally {
      setBusy("");
    }
  };

  const review = async (verdict, notes = "", retry = false) => {
    setBusy(verdict);
    try {
      const res = await fetch(`${API_BASE_URL}error-tracking/ai-fix/review/`, {
        method: "POST",
        headers,
        body: JSON.stringify({ job_id: job.job_id, verdict, notes, retry }),
      });
      const data = await res.json();
      if (data.status === "success") {
        const target = data.retry_job?.job_id || data.data.job_id;
        await loadDetail(target);
        notify(data.message, verdict === "accepted" ? "success" : "info");
        setRejectOpen(false);
        setRejectNotes("");
        if (onJobChange) onJobChange(data.data);
      } else {
        notify(data.message || "Could not record the review", "danger");
      }
    } catch (err) {
      notify("Could not reach the server", "danger");
    } finally {
      setBusy("");
    }
  };

  const sendAnswers = async () => {
    const list = Object.keys(answers)
      .sort((a, b) => Number(a) - Number(b))
      .map((k) => answers[k])
      .filter((v) => (v || "").trim());
    if (!list.length && !instruction.trim()) return;
    setBusy("answer");
    try {
      const res = await fetch(`${API_BASE_URL}error-tracking/ai-fix/answer/`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          job_id: job.job_id,
          answers: list,
          instruction: instruction.trim(),
        }),
      });
      const data = await res.json();
      if (data.status === "success") {
        setAnswers({});
        setInstruction("");
        setAskOpen(false);
        await loadDetail(job.job_id);
        notify(data.message, "success");
        if (onJobChange) onJobChange(data.data);
      } else {
        notify(data.message || "Could not send your input", "danger");
      }
    } catch (err) {
      notify("Could not reach the server", "danger");
    } finally {
      setBusy("");
    }
  };

  const cancel = async () => {
    setBusy("cancel");
    try {
      const res = await fetch(`${API_BASE_URL}error-tracking/ai-fix/cancel/`, {
        method: "POST",
        headers,
        body: JSON.stringify({ job_id: job.job_id }),
      });
      const data = await res.json();
      if (data.status === "success") {
        await loadDetail(job.job_id);
        notify(data.message, "info");
        if (onJobChange) onJobChange(data.data);
      } else {
        notify(data.message, "danger");
      }
    } catch (err) {
      notify("Could not reach the server", "danger");
    } finally {
      setBusy("");
    }
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 1500);
  };

  /* ── Rendering ──────────────────────────────────────────────────────── */

  const activeStepIndex = () => {
    if (!job) return -1;
    if (job.status === "branch_ready") return STEPS.length;
    const idx = STEPS.findIndex(s => s.match.includes(job.status));
    return idx;
  };

  const renderStepper = () => {
    const active = activeStepIndex();
    if (active < 0) return null;
    return (
      <div className="aifix-steps">
        {STEPS.map((step, i) => (
          <div
            key={step.key}
            className={`aifix-step ${i < active ? "aifix-step--done" : ""} ${i === active ? "aifix-step--active" : ""}`}
          >
            <span className="aifix-step__dot" />
            <span className="aifix-step__label">{step.label}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderDiff = (diff) => {
    if (!diff) return null;
    return (
      <pre className="aifix-diff">
        {diff.split("\n").map((line, i) => {
          let cls = "aifix-diff__ctx";
          if (line.startsWith("+++") || line.startsWith("---")) cls = "aifix-diff__meta";
          else if (line.startsWith("@@")) cls = "aifix-diff__hunk";
          else if (line.startsWith("+")) cls = "aifix-diff__add";
          else if (line.startsWith("-")) cls = "aifix-diff__del";
          else if (line.startsWith("diff ") || line.startsWith("index ")) cls = "aifix-diff__meta";
          return <div key={i} className={cls}>{line || " "}</div>;
        })}
      </pre>
    );
  };

  const renderLiveLog = () => {
    if (!events.length) return null;
    return (
      <div className="aifix-log" ref={logRef}>
        {events.map((e, i) => (
          <div key={`${e.seq}-${i}`} className={`aifix-log__line aifix-log__line--${e.level}`}>
            <span className="aifix-log__phase">{e.phase}</span>
            <span className="aifix-log__msg">{e.message}</span>
          </div>
        ))}
      </div>
    );
  };

  /* Empty state — no job has ever run for this pattern. */
  if (!job) {
    return (
      <div className="el-detail-section aifix">
        <h4>
          AI Fix
          <span className="aifix-hint">Analyse the backend code and prepare a fix</span>
        </h4>
        {message && <div className={`aifix-msg aifix-msg--${message.tone}`}>{message.text}</div>}
        <div className="aifix-empty">
          <div className="aifix-empty__text">
            The agent reads this error's traceback, opens the real files in
            <code> emedhubapiv4</code>, branches off <code>master</code>, fixes the
            code and commits locally. Nothing is pushed anywhere — you get a branch
            to check out.
          </div>
          <div className="aifix-empty__actions">
            <select className="el-stage-select" value={mode} onChange={(e) => setMode(e.target.value)}>
              <option value="patch">Analyse &amp; write fix</option>
              <option value="analyze">Analyse only</option>
            </select>
            <button
              className="el-btn el-btn--primary"
              onClick={() => enqueue()}
              disabled={loading || busy === "enqueue"}
            >
              <FiZap size={13} /> {busy === "enqueue" ? "Queueing..." : "Fix with AI"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const tone = STATUS_TONE[job.status] || "info";
  const isWorking = OPEN_STATUSES.includes(job.status);
  const hasBranch = ["branch_ready", "needs_review"].includes(job.status) && job.branch_name;
  const canReview = hasBranch && !job.review_verdict;
  const canRetry = ["failed", "rejected", "cancelled", "no_code_fix"].includes(job.status);
  // Input can be given to any finished job that has not been accepted — including
  // one the agent called "not a code bug", where telling it to look again is
  // exactly the useful thing to do.
  const canGiveInput = !isWorking && job.review_verdict !== "accepted";

  /*
   * The answer form lives next to the action buttons, not up beside the
   * analysis. Rendered near the top it opened hundreds of pixels above the
   * button that opened it, so clicking "Give more input" looked like it did
   * nothing at all.
   */
  const renderAskForm = () => {
    if (!canGiveInput || !(awaitingAnswers || askOpen)) return null;
    return (
      <div className="aifix-ask" ref={askRef}>
          {awaitingAnswers ? (
            <>
              <label className="aifix-ask__title">
                The agent needs your input to finish this fix. Answer what you can — it will
                continue on the same branch and keep the commit it already made.
              </label>
              {conversation
                .map((c, i) => ({ ...c, i }))
                .filter((c) => c.kind === "question")
                .slice(-6)
                .map((q, idx) => (
                  <div key={q.seq} className="aifix-ask__q">
                    <div className="aifix-ask__qtext">{q.text}</div>
                    <textarea
                      rows={2}
                      placeholder="Your answer..."
                      value={answers[idx] || ""}
                      onChange={(e) => setAnswers((p) => ({ ...p, [idx]: e.target.value }))}
                    />
                  </div>
                ))}
            </>
          ) : (
            <label className="aifix-ask__title">
              Tell the agent what to change. It will continue on the same branch.
            </label>
          )}

          <div className="aifix-ask__q">
            <div className="aifix-ask__qtext">
              {awaitingAnswers ? "Anything else it should know (optional)" : "Instruction"}
            </div>
            <textarea
              rows={2}
              placeholder="e.g. also handle the case where tenant_db is null"
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
            />
          </div>

          <div className="aifix-actions">
            <button
              className="el-btn el-btn--primary"
              onClick={sendAnswers}
              disabled={
                busy === "answer" ||
                (!instruction.trim() && !Object.values(answers).some((v) => (v || "").trim()))
              }
            >
              <FiMessageSquare size={13} />{" "}
              {busy === "answer" ? "Sending..." : "Send & continue"}
            </button>
            {!awaitingAnswers && (
              <button className="el-btn el-btn--ghost" onClick={() => setAskOpen(false)}>
                Cancel
              </button>
            )}
          </div>
        </div>
    );
  };


  return (
    <div className="el-detail-section aifix">
      <h4>
        AI Fix
        <span className={`aifix-status aifix-status--${tone}`}>
          {isWorking && <span className="aifix-spinner" />}
          {job.status_label}
        </span>
        <span className="aifix-jobid">job #{job.job_id}</span>
        {job.retry_of && <span className="aifix-jobid">retry of #{job.retry_of}</span>}
      </h4>

      {message && <div className={`aifix-msg aifix-msg--${message.tone}`}>{message.text}</div>}

      {renderStepper()}

      {job.status_detail && <div className="aifix-detailline">{job.status_detail}</div>}

      {/* Live progress while a runner works */}
      {isWorking && (
        <>
          {renderLiveLog()}
          <div className="aifix-actions">
            <button className="el-btn el-btn--ghost" onClick={cancel} disabled={busy === "cancel"}>
              <FiSlash size={13} /> Cancel
            </button>
          </div>
        </>
      )}

      {/* Questions from the agent, and the developer's replies */}
      {conversation.length > 0 && (
        <div className="aifix-thread">
          {conversation.map((c, i) => (
            <div key={`${c.seq}-${i}`} className={`aifix-thread__item aifix-thread__item--${c.kind}`}>
              <span className="aifix-thread__tag">
                {c.kind === "question" ? "Agent asks" : c.kind === "answer" ? "You answered" : "Your instruction"}
              </span>
              <div className="aifix-thread__text">{c.text}</div>
            </div>
          ))}
        </div>
      )}


      {/* Guard warnings */}
      {job.guard_flags?.length > 0 && (
        <div className="aifix-guards">
          <div className="aifix-guards__title">
            <FiAlertTriangle size={13} /> {job.guard_flags.length} guard warning(s) — a human decision is required
          </div>
          <ul>
            {job.guard_flags.map((g, i) => <li key={i}>{g}</li>)}
          </ul>
        </div>
      )}

      {/* Not a code bug */}
      {job.status === "no_code_fix" && (
        <div className="el-detail-block el-detail-block--warning">
          <strong>The agent concluded this is not a code defect.</strong>
          {job.human_reason && <div className="aifix-spaced">{job.human_reason}</div>}
        </div>
      )}

      {/* Analysis */}
      {job.root_cause && (
        <div className="aifix-block">
          <span className="aifix-block__label">Root cause</span>
          <div className="el-detail-block el-detail-block--warning">{job.root_cause}</div>
        </div>
      )}
      {job.fix_summary && (
        <div className="aifix-block">
          <span className="aifix-block__label">Fix</span>
          <div className="el-detail-block el-detail-block--success">{job.fix_summary}</div>
        </div>
      )}

      {/* Meta chips */}
      {(job.confidence || job.files_changed?.length > 0 || job.verify_passed !== null) && (
        <div className="aifix-chips">
          {job.confidence && (
            <span className={`aifix-chip aifix-chip--${CONFIDENCE_TONE[job.confidence] || "info"}`}>
              {job.confidence} confidence
            </span>
          )}
          {job.verify_passed === true && <span className="aifix-chip aifix-chip--success">checks passed</span>}
          {job.verify_passed === false && <span className="aifix-chip aifix-chip--danger">checks failed</span>}
          {job.files_changed?.length > 0 && (
            <span className="aifix-chip">{job.files_changed.length} file(s) changed</span>
          )}
          {job.num_turns > 0 && <span className="aifix-chip aifix-chip--muted">{job.num_turns} turns</span>}
          {job.duration_seconds > 0 && (
            <span className="aifix-chip aifix-chip--muted">{job.duration_seconds}s</span>
          )}
        </div>
      )}

      {job.risk_notes && (
        <div className="aifix-block">
          <span className="aifix-block__label">Review notes from the agent</span>
          <div className="el-detail-block">{job.risk_notes}</div>
        </div>
      )}

      {job.files_changed?.length > 0 && (
        <div className="aifix-files">
          {job.files_changed.map((f, i) => <code key={i}>{f}</code>)}
        </div>
      )}

      {/* Diff */}
      {job.diff && (
        <div className="aifix-block">
          <button className="aifix-toggle" onClick={() => setShowDiff(v => !v)}>
            {showDiff ? <FiChevronUp size={13} /> : <FiChevronDown size={13} />} Diff
            <span className="aifix-toggle__hint">{job.diff.split("\n").length} lines</span>
          </button>
          {showDiff && renderDiff(job.diff)}
        </div>
      )}

      {/* Verification output */}
      {job.verify_output && (
        <div className="aifix-block">
          <span className="aifix-block__label">Verification</span>
          <pre className="el-detail-code">{job.verify_output}</pre>
        </div>
      )}

      {/* Branch result — it exists locally, ready to check out */}
      {hasBranch && (
        <div className="aifix-branch">
          <div className="aifix-branch__row">
            <FiGitBranch size={14} />
            <code>{job.branch_name}</code>
            <button className="el-copy-btn" onClick={() => copy(job.branch_name, "branch")} title="Copy branch name">
              {copied === "branch" ? <FiCheck size={11} /> : <FiCopy size={11} />}
            </button>
          </div>
          <div className="aifix-branch__cmd">
            <code>git checkout {job.branch_name}</code>
            <button
              className="el-copy-btn"
              onClick={() => copy(`git checkout ${job.branch_name}`, "cmd")}
              title="Copy checkout command"
            >
              {copied === "cmd" ? <FiCheck size={11} /> : <FiCopy size={11} />}
            </button>
          </div>
          {job.commit_sha && (
            <div className="aifix-branch__sha">
              commit {job.commit_sha.slice(0, 12)} &middot; branched from {job.base_branch}
              {job.pushed_at ? " · pushed to origin" : " · local only"}
            </div>
          )}
          {job.branch_url && (
            <div className="aifix-branch__links">
              <a className="el-btn el-btn--outline" href={job.branch_url} target="_blank" rel="noreferrer">
                <FiExternalLink size={12} /> View on GitLab
              </a>
            </div>
          )}
        </div>
      )}

      {/* Failure detail */}
      {job.error_message && (
        <div className="aifix-block">
          <span className="aifix-block__label">Error</span>
          <pre className="el-detail-code aifix-error">{job.error_message}</pre>
        </div>
      )}

      {/* Agent transcript */}
      {job.agent_log && (
        <div className="aifix-block">
          <button className="aifix-toggle" onClick={() => setShowLog(v => !v)}>
            {showLog ? <FiChevronUp size={13} /> : <FiChevronDown size={13} />} What the agent did
          </button>
          {showLog && <pre className="el-detail-code aifix-transcript">{job.agent_log}</pre>}
        </div>
      )}

      {/* Human verdict already recorded */}
      {job.review_verdict && (
        <div className={`aifix-verdict aifix-verdict--${job.review_verdict}`}>
          {job.review_verdict === "accepted" ? <FiCheck size={13} /> : <FiX size={13} />}
          <span>
            {job.review_verdict === "accepted" ? "Accepted" : "Rejected"}
            {job.reviewed_by_name ? ` by ${job.reviewed_by_name}` : ""}
          </span>
          {job.review_notes && <div className="aifix-verdict__notes">{job.review_notes}</div>}
        </div>
      )}

      {/* Review actions */}
      {renderAskForm()}

      {canReview && !rejectOpen && (
        <div className="aifix-actions">
          <button
            className="el-btn el-btn--success"
            onClick={() => review("accepted")}
            disabled={busy === "accepted"}
          >
            <FiUploadCloud size={13} /> {busy === "accepted" ? "Pushing..." : "Accept & push branch"}
          </button>
          <button className="el-btn el-btn--ghost" onClick={() => setRejectOpen(true)}>
            <FiX size={13} /> Reject
          </button>
          {!askOpen && !awaitingAnswers && (
            <button className="el-btn el-btn--outline" onClick={() => setAskOpen(true)}>
              <FiMessageSquare size={13} /> Give more input
            </button>
          )}
          <span className="aifix-actions__hint">
            Accepting pushes the branch to origin and moves the error to In Dev.
          </span>
        </div>
      )}

      {canReview && rejectOpen && (
        <div className="aifix-reject">
          <label>Why is this fix wrong? The agent receives this on retry.</label>
          <textarea
            rows={3}
            value={rejectNotes}
            placeholder="e.g. This just hides the error — the amount key should always be present."
            onChange={(e) => setRejectNotes(e.target.value)}
          />
          <div className="aifix-actions">
            <button
              className="el-btn el-btn--primary"
              onClick={() => review("rejected", rejectNotes, true)}
              disabled={busy === "rejected" || !rejectNotes.trim()}
            >
              <FiRefreshCw size={13} /> Reject &amp; retry
            </button>
            <button
              className="el-btn el-btn--outline"
              onClick={() => review("rejected", rejectNotes, false)}
              disabled={busy === "rejected"}
            >
              Reject only
            </button>
            <button className="el-btn el-btn--ghost" onClick={() => setRejectOpen(false)}>Cancel</button>
          </div>
        </div>
      )}

      {renderAskForm()}

      {canRetry && (
        <div className="aifix-actions">
          <button
            className="el-btn el-btn--outline"
            onClick={() => enqueue(job.job_id)}
            disabled={busy === "enqueue"}
          >
            <FiRefreshCw size={13} /> {busy === "enqueue" ? "Queueing..." : "Run again"}
          </button>
          {!askOpen && !awaitingAnswers && (
            <button className="el-btn el-btn--outline" onClick={() => setAskOpen(true)}>
              <FiMessageSquare size={13} /> Give more input
            </button>
          )}
          <span className="aifix-actions__hint">
            "Give more input" continues this job; "Run again" starts a fresh attempt.
          </span>
        </div>
      )}
    </div>
  );
};

export default AIFixPanel;
